import { Field, FieldProps, useFormikContext } from 'formik';
import { FormHelperText, IconButton, styled } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PlusCircleIcon } from '@synergy/theme';
import {
    PoolButton,
    PoolSelect,
    PoolTable,
    PoolTableColumn,
    PoolTableRow,
    PoolTextField,
    SelectOption,
} from '@synergy/components';
import { fields } from '@synergy/utils';

import { PackingUnit } from '../models/packing-unit';
import { ProductDetailOverviewFormValues } from '../models/product-detail-overview-form-values';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const StyledPoolTable = styled(PoolTable)(({ theme }) => ({
    marginBottom: theme.spacing(2),

    '& tr': {
        verticalAlign: 'top',

        // HACK: change with table design issue
        '& td:last-child > button': {
            marginTop: 18,
        },
    },

    '& th:not(:last-child)': {
        width: '33.2%',
    },

    '& td:not(:last-child)': {
        width: '33.2%',
    },

    '& th:last-child': {
        padding: 0,
    },

    '& td:last-child': {
        padding: 0,
        paddingRight: 16,
    },
}));

type PackingUnitFormValues = Partial<PackingUnit>;

export const ProductDetailOverviewSectionPackingUnits = () => {
    const { allUnits: units, requesting: unitsRequesting } = useAppSelector(state => state.units);
    const bestLocale = useAppSelector(state => bestLocaleSelector(state));
    const intl = useIntl();
    const formik = useFormikContext<ProductDetailOverviewFormValues>();
    const formValuesFieldNames = fields<ProductDetailOverviewFormValues>();
    const fieldNames = fields<PackingUnit>();

    const selectableUnits = units.map<SelectOption>(unit => {
        return {
            value: unit.id,
            displayValue: unit.translations[bestLocale],
        };
    });
    selectableUnits.unshift({
        value: 'undefined',
        displayValue: <FormattedMessage id="app.select.option.noneSpecification" />,
    });

    const columns: PoolTableColumn[] = [
        {
            id: 'packingUnit',
            label: <FormattedMessage id="app.product.detail.prices.packingUnit" />,
            sortable: false,
            width: '50%',
        },
        {
            id: 'minimumQuantity',
            label: <FormattedMessage id="app.label.minimumQuantity" />,
            sortable: false,
            width: '25%',
        },
        {
            id: 'maximumQuantity',
            label: <FormattedMessage id="app.label.maximumQuantity" />,
            sortable: false,
            width: '25%',
        },
        {
            id: 'action',
            sortable: false,
            width: '1%',
        },
    ];

    const handleRemove = (rowIndex: number) => {
        const items: PackingUnitFormValues[] = [...formik.values.packingUnits];

        if (items.length === 1) {
            formik.setFieldValue(formValuesFieldNames.packingUnits, [
                { id: -1, minimumQuantity: '', maximumQuantity: '' },
            ]);
            return;
        }

        items.splice(rowIndex, 1);
        formik.setFieldValue(formValuesFieldNames.packingUnits, items);
    };

    const getNextId = (): number => {
        return formik.values.packingUnits.reduce((acc, item) => Math.min(acc, item.id || 0), 0) - 1;
    };

    const handleAddRow = () => {
        formik.setFieldValue(formValuesFieldNames.packingUnits, [...formik.values.packingUnits, { id: getNextId() }]);
    };

    const getRows = () => {
        const rows: PoolTableRow[] = formik.values.packingUnits.map((item, rowIndex) => {
            return {
                id: item.id,
                packingUnit: (
                    <Field name={`${formValuesFieldNames.packingUnits}.${rowIndex}.${fieldNames.unitId}`}>
                        {({ form, field, meta }: FieldProps<number>) => (
                            <>
                                <PoolSelect
                                    name={`${formValuesFieldNames.packingUnits}[${rowIndex}].unitId`}
                                    fullWidth={true}
                                    options={selectableUnits}
                                    onValueChange={value => form.setFieldValue(field.name, value)}
                                    error={meta.touched && !!meta.error}
                                    required={true}
                                    value={field.value ?? 'undefined'}
                                    noSorting={true}
                                    disabled={unitsRequesting}
                                    disallowDefaultValue={true}
                                />
                                {meta.error && (
                                    <FormHelperText error={true}>
                                        <FormattedMessage id={meta.error} />
                                    </FormHelperText>
                                )}
                            </>
                        )}
                    </Field>
                ),
                minimumQuantity: (
                    <Field name={`${formValuesFieldNames.packingUnits}.${rowIndex}.${fieldNames.minimumQuantity}`}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                placeholder={intl.formatMessage({ id: 'app.label.minimumQuantity' })}
                                error={!!meta.error}
                                helperText={meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                                isNumeric={true}
                                noNegativeNumbers={true}
                            />
                        )}
                    </Field>
                ),
                maximumQuantity: (
                    <Field name={`${formValuesFieldNames.packingUnits}.${rowIndex}.${fieldNames.maximumQuantity}`}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                placeholder={intl.formatMessage({ id: 'app.label.maximumQuantity' })}
                                error={!!meta.error}
                                helperText={meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                                isNumeric={true}
                                noNegativeNumbers={true}
                            />
                        )}
                    </Field>
                ),
                action: (
                    <IconButton onClick={() => handleRemove(rowIndex)}>
                        <PlusCircleIcon color="primary" style={{ transform: 'rotate(45deg)' }} />
                    </IconButton>
                ),
            };
        });

        return rows;
    };

    return (
        <div>
            <StyledPoolTable columns={columns} rows={getRows()} styleType="bordered" />
            <PoolButton
                styleType="thirdLevel"
                onClick={handleAddRow}
                startIcon={<PlusCircleIcon color="primary" sx={{ fs: 20 }} />}
            >
                <FormattedMessage id="app.product.detail.addPackingUnit" />
            </PoolButton>
        </div>
    );
};
